import { useCallback } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useState } from "react";
import {
  Cropper as AdvancedCropper,
  type CropperRef as AdvancedCropperRef,
  ImageRestriction,
  RectangleStencil,
} from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import ReactDOM from "react-dom";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";
import Slider from "react-input-slider";

import { type Area } from "../../services/api";
import Modal, { ModalButton, ModalSecondaryButton } from "../Modal";
import { AdvancedCropContainer, CropContainer, ZoomControl } from "./styles";

export type ImageDisplayMode = "cover" | "contain";
export type CropMode = "fixed" | "free";
export type ImageCropResult = {
  area?: Area;
  displayMode: ImageDisplayMode;
  aspectRatio?: string;
};

type Props = {
  file: File;
  aspect: number;
  cropShape: "round" | "rect";
  cropMode?: CropMode;
  onSave: (result?: ImageCropResult) => void;
};

const sliderStyles = {
  track: {
    width: "140px",
    backgroundColor: "var(--color-neutral-base)",
  },
  active: {
    backgroundColor: "var(--color-neutral-base)",
  },
  thumb: {
    width: "24px",
    height: "24px",
  },
};

const maxZoom = 6;

export const ImageCropModal = ({ file, onSave, aspect, cropShape, cropMode = "fixed" }: Props) => {
  const { t } = useTranslation();
  const advancedCropperRef = useRef<AdvancedCropperRef>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

  const [objectFit, setObjectFit] = useState<"horizontal-cover" | "vertical-cover" | undefined>();

  const [src, setSrc] = useState<string | undefined>();

  const setFile = useCallback(async () => {
    const src = await readFileAsDataURL(file);
    const image = await createImage(src);
    setObjectFit(
      image.height < image.width ? "vertical-cover" : image.height > image.width ? "horizontal-cover" : undefined
    );
    setSrc(src);
  }, [file]);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onZoomChangeHandler = useCallback(({ x }: { x: number }) => {
    setZoom(x);
  }, []);

  const saveHandler = async () => {
    if (cropMode === "free") {
      const coordinates = advancedCropperRef.current?.getCoordinates();

      if (coordinates) {
        const area = {
          x: coordinates.left,
          y: coordinates.top,
          width: coordinates.width,
          height: coordinates.height,
        };
        const aspectRatio = `${Math.round(area.width)} / ${Math.round(area.height)}`;
        onSave({ area, displayMode: "cover", aspectRatio });
      }
      return;
    }

    if (src && croppedAreaPixels) {
      const aspectRatio = `${Math.round(croppedAreaPixels.width)} / ${Math.round(croppedAreaPixels.height)}`;
      onSave({ area: croppedAreaPixels, displayMode: "cover", aspectRatio });
    }
  };

  const useOriginalHandler = () => {
    onSave({ displayMode: "contain" });
  };

  useEffect(() => {
    setFile();
  }, [file, setFile]);

  return ReactDOM.createPortal(
    <Modal
      flush
      title={t("actions.resizeImage")}
      subtitle={t(cropMode === "free" ? "actions.resizeImageFreeHint" : "actions.resizeImageHint")}
      onExit={onSave}
      footer={
        <>
          {cropMode === "free" ? (
            <ModalSecondaryButton type="button" onClick={useOriginalHandler}>
              {t("actions.useFullImage")}
            </ModalSecondaryButton>
          ) : (
            <ZoomControl>
              <Slider
                xmin={1}
                xmax={maxZoom}
                axis="x"
                x={zoom}
                onChange={onZoomChangeHandler}
                styles={sliderStyles}
                xstep={0.01}
              />
            </ZoomControl>
          )}
          <ModalButton onClick={saveHandler}>{t("actions.cropAndSave")}</ModalButton>
        </>
      }>
      {cropMode === "free" ? (
        <AdvancedCropContainer>
          {src && (
            <AdvancedCropper
              ref={advancedCropperRef}
              src={src}
              stencilComponent={RectangleStencil}
              stencilProps={{
                movable: true,
                resizable: true,
                grid: false,
                handlerClassNames: {
                  default: "keepo-crop-handler",
                },
                lineClassNames: {
                  default: "keepo-crop-line",
                },
              }}
              imageRestriction={ImageRestriction.stencil}
              className="advanced-image-cropper"
            />
          )}
        </AdvancedCropContainer>
      ) : (
        <CropContainer $aspect={aspect}>
          {src && (
            <Cropper
              image={src}
              crop={crop}
              cropShape={cropShape}
              zoom={zoom}
              maxZoom={maxZoom}
              zoomSpeed={8}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              objectFit={objectFit}
              style={{
                cropAreaStyle: {
                  border: "none",
                },
              }}
            />
          )}
        </CropContainer>
      )}
    </Modal>,
    document.getElementById("root") as Element
  );
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", error => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      resolve(reader.result as string);
    });
    reader.addEventListener("error", reject);
    reader.readAsDataURL(file);
  });
